import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Download } from "lucide-react";
import { api } from "../api";
import { reportPurchase } from "../lib/gtag";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking" });
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error" });
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", token: data.download_token });
          if (data.amount) {
            reportPurchase({ value: data.amount / 100, currency: data.currency, transactionId: sessionId });
          }
          return;
        }
        if (data.payment_status === "failed" || data.status === "expired") {
          setState({ phase: "error" });
          return;
        }
      } catch {
        /* keep polling */
      }
      tries.current += 1;
      if (tries.current < 30) timer = setTimeout(poll, 2000);
      else setState({ phase: "error" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-6 py-24 text-center" data-testid="payment-success-page">
      {state.phase === "checking" && (
        <>
          <Loader2 className="animate-spin text-sage" size={36} />
          <h1 className="mt-6 font-serif text-3xl text-ink">Confirming your payment…</h1>
          <p className="mt-3 text-sm text-ink/55">This takes just a moment.</p>
        </>
      )}
      {state.phase === "paid" && (
        <>
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-sage-tint text-sage">
            <CheckCircle2 size={32} />
          </span>
          <h1 className="mt-6 font-serif text-4xl font-light text-ink" data-testid="payment-confirmed-title">
            Thank you — your order is confirmed
          </h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-ink/60">
            Your payment was verified and a receipt with your download link is on its way to your
            inbox. You can also download right now:
          </p>
          {state.token && (
            <Link
              to={`/download/${state.token}`}
              data-testid="success-download-link"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-sage px-8 py-4 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-sage-deep"
            >
              <Download size={16} /> Go to your download
            </Link>
          )}
        </>
      )}
      {state.phase === "error" && (
        <>
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-lavender-tint text-lavender-deep">
            <XCircle size={32} />
          </span>
          <h1 className="mt-6 font-serif text-3xl text-ink" data-testid="payment-unconfirmed-title">
            We couldn't confirm that payment
          </h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-ink/60">
            If you completed checkout, your receipt and download link will still arrive by email
            once Stripe confirms the payment. Otherwise no charge was made.
          </p>
          <Link to="/shop" className="mt-8 rounded-full border border-sage/30 px-8 py-3.5 text-sm text-sage transition-colors hover:bg-sage hover:text-cream">
            Back to the shop
          </Link>
        </>
      )}
    </div>
  );
}
